/**
 * TAVEN Live — Beauty & Camera filter engine.
 * Beauty (brightness / smooth skin / warmth) runs through a lightweight canvas
 * transformer that keeps product colors true and adds near-zero latency.
 * Real background blur uses LiveKit's ML segmentation (product held close to the
 * camera stays sharp). A Low-Performance mode disables the heavy ML path.
 */
import {
  VideoTransformer,
  ProcessorWrapper,
  BackgroundProcessor,
  supportsBackgroundProcessors,
} from "@livekit/track-processors";

// Lightweight CSS-style beauty transformer — applied to the broadcast track.
class BeautyTransformer extends VideoTransformer {
  constructor(opts = {}) {
    super();
    this.options = { brightness: 1, contrast: 1, saturate: 1, smooth: 0, ...opts };
  }

  async init({ outputCanvas, inputElement }) {
    if (!(inputElement instanceof HTMLVideoElement)) {
      throw new TypeError("BeautyTransformer needs an HTMLVideoElement input");
    }
    this.transformer = new TransformStream({
      transform: (frame, controller) => this.transform(frame, controller),
    });
    this.canvas = outputCanvas;
    this.ctx = outputCanvas.getContext("2d");
    this.inputVideo = inputElement;
    this.isDisabled = false;
  }

  _filter() {
    const { brightness, contrast, saturate, smooth } = this.options;
    const parts = [];
    if (brightness !== 1) parts.push(`brightness(${brightness})`);
    if (contrast !== 1) parts.push(`contrast(${contrast})`);
    if (saturate !== 1) parts.push(`saturate(${saturate})`);
    if (smooth > 0) parts.push(`blur(${smooth}px)`);
    return parts.length ? parts.join(" ") : "none";
  }

  transform(frame, controller) {
    if (this.isDisabled || !this.canvas || !this.ctx) {
      controller.enqueue(frame);
      return;
    }
    try {
      const w = frame.displayWidth;
      const h = frame.displayHeight;
      if (this.canvas.width !== w) this.canvas.width = w;
      if (this.canvas.height !== h) this.canvas.height = h;
      this.ctx.filter = this._filter();
      this.ctx.drawImage(frame, 0, 0, w, h);
      this.ctx.filter = "none";
      const next = new VideoFrame(this.canvas, { timestamp: frame.timestamp });
      frame.close();
      controller.enqueue(next);
    } catch (e) {
      controller.enqueue(frame);
    }
  }

  async update(opts) {
    this.options = { ...this.options, ...opts };
  }

  async destroy() {
    this.isDisabled = true;
    this.canvas = undefined;
    this.ctx = undefined;
  }
}

export function createBeautyProcessor(opts) {
  return new ProcessorWrapper(new BeautyTransformer(opts), "taven-beauty", { maxFps: 30 });
}

export function createBlurProcessor(blurRadius = 8) {
  return BackgroundProcessor({ mode: "background-blur", blurRadius });
}

export function canRealBlur() {
  try {
    return supportsBackgroundProcessors();
  } catch {
    return false;
  }
}

// Heuristic for weak devices — auto-enables Low Performance mode.
export function isLowPerfDevice() {
  const cores = navigator.hardwareConcurrency || 4;
  const mem = navigator.deviceMemory || 4;
  return cores <= 4 || mem <= 3;
}

/**
 * Map raw slider/toggle state → transformer options.
 * Product Accurate Mode: no color shift (saturate=1), no skin blur, and
 * brightness clamped so product colors/details stay true.
 */
export function mapBeautyOptions(fx) {
  const b = (fx.brightness || 0) / 100;
  const s = (fx.smooth || 0) / 100;
  const w = (fx.warmth || 0) / 100;
  if (fx.productAccurate) {
    return { brightness: 1 + b * 0.15, contrast: 1, saturate: 1, smooth: 0 };
  }
  return {
    brightness: 1 + b * 0.4,
    contrast: 1 + b * 0.05,
    saturate: 1 + w * 0.35,
    smooth: s * 2.5,
  };
}
